'use client';

/**
 * Prompt Library Modal
 *
 * Full-featured prompt management interface with liquid glass design:
 * - Browse all prompts with search and filters
 * - View prompts by category, source (system/org/user), favorites, recent
 * - Create, edit, and delete user prompts
 * - View org prompts (read-only for non-admins)
 * - Favorite any prompt for quick access
 * - Rich prompt preview with variable detection
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:prompt-library-modal');
import {
  X,
  Search,
  Star,
  Clock,
  User,
  Building2,
  Sparkles,
  Plus,
  ChevronDown,
  Eye,
  Pencil,
  Trash2,
  Check,
} from 'lucide-react';
import type { PromptTemplate, PromptCategory } from '@ctrl-enter/types';
import {
  PROMPT_CATEGORY_LABELS,
  PROMPT_CATEGORY_ICONS,
  PROMPT_CATEGORIES,
} from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { Dialog } from './dialog';
import {
  usePromptLibraryStore,
  useUserPrompts,
  useOrgPrompts,
  useSystemPrompts,
  useIsLibraryModalOpen,
} from '../stores/prompt-library-store';
import {
  useAllPrompts,
  useFavoritePrompts,
  useRecentlyUsedPrompts,
} from '../hooks/use-prompt-library';
import { searchPrompts, sortPrompts } from '../utils/prompt-utils';
import { PromptCategoryIcon } from './prompt-category-icon';
import { Button } from './button';
import { useDeleteUserPromptMutation } from '../hooks/use-gateway-queries';
import { toast } from '../utils/toast';

type PromptLibraryModalProps = {
  onSelectPrompt?: (prompt: PromptTemplate) => void; // Optional - only for chat dropdown
  onCreatePrompt?: () => void;
  onEditPrompt?: (prompt: PromptTemplate) => void;
};

type ViewMode = 'all' | 'favorites' | 'recent' | 'user' | 'org' | 'system' | PromptCategory;

export const PromptLibraryModal: React.FC<PromptLibraryModalProps> = ({
  onSelectPrompt,
  onCreatePrompt,
  onEditPrompt,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [sortBy, setSortBy] = useState<'title' | 'recent' | 'popular'>('recent');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  const isOpen = useIsLibraryModalOpen();

  // ✅ CORRECT - Use selectors, not destructuring (per .cursorrules)
  const closeLibraryModal = usePromptLibraryStore(state => state.closeLibraryModal);
  const toggleFavorite = usePromptLibraryStore(state => state.toggleFavorite);
  const isFavorite = usePromptLibraryStore(state => state.isFavorite);
  const deleteUserPromptFromStore = usePromptLibraryStore(state => state.deleteUserPrompt);
  const isPromptsLoading = usePromptLibraryStore(state => state.isPromptsLoading);
  const isPreferencesLoading = usePromptLibraryStore(state => state.isPreferencesLoading);

  // Backend API mutation
  const deleteMutation = useDeleteUserPromptMutation();

  // Get prompts from store
  const allPrompts = useAllPrompts();
  const favoritePrompts = useFavoritePrompts();
  const recentPrompts = useRecentlyUsedPrompts();
  const userPrompts = useUserPrompts();
  const orgPrompts = useOrgPrompts();
  const systemPrompts = useSystemPrompts();

  // Show loading if either prompts or preferences are loading
  const isLoading = isPromptsLoading || isPreferencesLoading;

  // Filter and sort prompts based on view mode
  const displayedPrompts = useMemo(() => {
    let prompts: PromptTemplate[] = [];

    // Filter by view mode
    switch (viewMode) {
      case 'all':
        prompts = allPrompts;
        break;
      case 'favorites':
        prompts = favoritePrompts;
        break;
      case 'recent':
        prompts = recentPrompts;
        break;
      case 'user':
        prompts = userPrompts;
        break;
      case 'org':
        prompts = orgPrompts;
        break;
      case 'system':
        prompts = systemPrompts;
        break;
      default:
        // Category view
        prompts = allPrompts.filter(p => p.category === viewMode);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      prompts = searchPrompts(prompts, searchQuery);
    }

    // Sort
    prompts = sortPrompts(prompts, sortBy);

    return prompts;
  }, [
    viewMode,
    searchQuery,
    sortBy,
    allPrompts,
    favoritePrompts,
    recentPrompts,
    userPrompts,
    orgPrompts,
    systemPrompts,
  ]);

  // Get counts for sidebar
  const counts = useMemo(
    () => ({
      all: allPrompts.length,
      favorites: favoritePrompts.length,
      recent: recentPrompts.length,
      user: userPrompts.length,
      org: orgPrompts.length,
      system: systemPrompts.length,
      categories: PROMPT_CATEGORIES.reduce(
        (acc, cat) => {
          acc[cat] = allPrompts.filter(p => p.category === cat).length;
          return acc;
        },
        {} as Record<PromptCategory, number>
      ),
    }),
    [allPrompts, favoritePrompts, recentPrompts, userPrompts, orgPrompts, systemPrompts]
  );

  // Close sort dropdown when clicking outside
  useEffect(() => {
    if (!isSortDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortDropdownOpen]);

  const handleClose = () => {
    closeLibraryModal();
    setSearchQuery('');
    setViewMode('all');
    setSelectedPrompt(null);
  };

  const handlePromptClick = (prompt: PromptTemplate) => {
    setSelectedPrompt(prompt);
  };

  const handleUsePrompt = () => {
    if (selectedPrompt) {
      onSelectPrompt?.(selectedPrompt);
      handleClose();
    }
  };

  const handleToggleFavorite = (prompt: PromptTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(prompt.id);
  };

  const handleDelete = async (prompt: PromptTemplate, e: React.MouseEvent) => {
    e.stopPropagation();

    // System prompts cannot be deleted
    if (prompt.source === 'system') return;

    if (!confirm(`Delete prompt "${prompt.title}"?`)) return;

    try {
      // Delete user prompt from backend
      await deleteMutation.mutateAsync({
        params: { promptId: prompt.id },
      });
      // Remove from local store
      deleteUserPromptFromStore(prompt.id);
      toast.success('Prompt deleted', `"${prompt.title}" has been removed`);

      if (selectedPrompt?.id === prompt.id) {
        setSelectedPrompt(null);
      }
    } catch (error) {
      logger.error('Failed to delete prompt:', error);
      const message =
        error instanceof Error ? error.message : 'Could not delete prompt from server';
      toast.error('Delete failed', message);
    }
  };

  const handleEdit = (prompt: PromptTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditPrompt) {
      onEditPrompt(prompt);
      handleClose();
    }
  };

  const getSourceBadge = (source: PromptTemplate['source']) => {
    switch (source) {
      case 'system':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/10 text-blue-500 rounded-lg">
            <Sparkles className="h-3 w-3" />
            System
          </span>
        );
      case 'org':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-500/10 text-purple-500 rounded-lg">
            <Building2 className="h-3 w-3" />
            Organization
          </span>
        );
      case 'user':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500 rounded-lg">
            <User className="h-3 w-3" />
            Personal
          </span>
        );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      aria-labelledby="prompt-library-title"
      className="h-[85vh] flex flex-col overflow-hidden"
    >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 id="prompt-library-title" className="text-xl font-semibold text-foreground">Prompt Library</h2>
              <p className="text-sm text-muted-foreground">
                {displayedPrompts.length} prompt{displayedPrompts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Create button */}
            {onCreatePrompt && (
              <Button variant="primary" size="md" onClick={onCreatePrompt}>
                <Plus className="h-4 w-4" />
                <span>Create Prompt</span>
              </Button>
            )}

            {/* Close button */}
            <Button onClick={handleClose} variant="ghost" size="icon" aria-label="Close">
              <X className="h-5 w-5 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="px-6 py-3 flex-shrink-0 border-none">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-10 pr-4 py-2 text-sm text-foreground',
                  'glass-surface border border-border/50 rounded-xl',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                  'transition-all duration-200'
                )}
              />
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sort:</span>
              <div className="relative" ref={sortDropdownRef}>
                <button
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className={cn(
                    'px-3 py-2 text-sm text-foreground',
                    'glass-surface border border-border/50 rounded-xl',
                    'hover:glass-surface-hover transition-all duration-200',
                    'flex items-center gap-2 min-w-[120px] justify-between'
                  )}
                >
                  <span>
                    {sortBy === 'recent' && 'Recent'}
                    {sortBy === 'title' && 'A-Z'}
                    {sortBy === 'popular' && 'Popular'}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform duration-200',
                      isSortDropdownOpen && 'rotate-180'
                    )}
                  />
                </button>

                {/* Custom dropdown menu */}
                {isSortDropdownOpen && (
                  <div
                    className={cn(
                      'absolute top-full mt-1 right-0 min-w-[120px] z-10',
                      'glass-surface border border-border/50 rounded-xl shadow-lg',
                      'py-1',
                      'animate-in fade-in-0 zoom-in-95 duration-200'
                    )}
                  >
                    {[
                      { value: 'recent', label: 'Recent' },
                      { value: 'title', label: 'A-Z' },
                      { value: 'popular', label: 'Popular' },
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value as 'title' | 'recent' | 'popular');
                          setIsSortDropdownOpen(false);
                        }}
                        className={cn(
                          'w-full px-3 py-2 text-left text-sm text-foreground',
                          'hover:glass-surface-hover transition-all duration-200',
                          'flex items-center justify-between',
                          'first:rounded-t-xl last:rounded-b-xl'
                        )}
                      >
                        <span>{option.label}</span>
                        {sortBy === option.value && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 min-h-0 p-3 gap-3">
          {/* Sidebar */}
          <div className="w-64 glass-card-bg glass-card-border rounded-2xl p-3 overflow-y-auto flex-shrink-0">
            <nav className="space-y-1">
              {/* Quick views */}
              <div className="mb-4">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Quick Access
                </div>
                <button
                  onClick={() => setViewMode('all')}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                    'hover:glass-surface-hover transition-all duration-300',
                    'flex items-center justify-between',
                    viewMode === 'all' &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  )}
                >
                  <span>All Prompts</span>
                  <span
                    className={cn(
                      'text-xs',
                      viewMode === 'all' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}
                  >
                    {counts.all}
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('favorites')}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                    'hover:glass-surface-hover transition-all duration-300',
                    'flex items-center justify-between',
                    viewMode === 'favorites' &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Star className="h-3.5 w-3.5" />
                    <span>Favorites</span>
                  </div>
                  <span
                    className={cn(
                      'text-xs',
                      viewMode === 'favorites'
                        ? 'text-primary-foreground/70'
                        : 'text-muted-foreground'
                    )}
                  >
                    {counts.favorites}
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('recent')}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                    'hover:glass-surface-hover transition-all duration-300',
                    'flex items-center justify-between',
                    viewMode === 'recent' &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Recently Used</span>
                  </div>
                  <span
                    className={cn(
                      'text-xs',
                      viewMode === 'recent' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}
                  >
                    {counts.recent}
                  </span>
                </button>
              </div>

              {/* Sources */}
              <div className="mb-4">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Source
                </div>
                <button
                  onClick={() => setViewMode('user')}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                    'hover:glass-surface-hover transition-all duration-300',
                    'flex items-center justify-between',
                    viewMode === 'user' &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5" />
                    <span>Personal</span>
                  </div>
                  <span
                    className={cn(
                      'text-xs',
                      viewMode === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}
                  >
                    {counts.user}
                  </span>
                </button>
                {counts.org > 0 && (
                  <button
                    onClick={() => setViewMode('org')}
                    className={cn(
                      'w-full px-3 py-2 text-left rounded-xl text-sm',
                      'hover:glass-surface-hover transition-all duration-300',
                      'flex items-center justify-between',
                      viewMode === 'org' &&
                        'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5" />
                      <span>Organization</span>
                    </div>
                    <span
                      className={cn(
                        'text-xs',
                        viewMode === 'org' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      )}
                    >
                      {counts.org}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setViewMode('system')}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                    'hover:glass-surface-hover transition-all duration-300',
                    'flex items-center justify-between',
                    viewMode === 'system' &&
                      'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>System</span>
                  </div>
                  <span
                    className={cn(
                      'text-xs',
                      viewMode === 'system' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}
                  >
                    {counts.system}
                  </span>
                </button>
              </div>

              {/* Categories */}
              <div>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Categories
                </div>
                {PROMPT_CATEGORIES.map(category => {
                  const count = counts.categories[category];
                  if (count === 0) return null;

                  return (
                    <button
                      key={category}
                      onClick={() => setViewMode(category)}
                      className={cn(
                        'w-full px-3 py-2 text-left rounded-xl text-sm text-foreground',
                        'hover:glass-surface-hover transition-all duration-300',
                        'flex items-center justify-between',
                        viewMode === category &&
                          'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <PromptCategoryIcon iconName={PROMPT_CATEGORY_ICONS[category]} size={16} />
                        <span>{PROMPT_CATEGORY_LABELS[category]}</span>
                      </div>
                      <span
                        className={cn(
                          'text-xs',
                          viewMode === category
                            ? 'text-primary-foreground/70'
                            : 'text-muted-foreground'
                        )}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Prompt list */}
          <div className="flex-1 flex min-w-0">
            {/* List panel */}
            <div className="w-2/5 glass-card-bg glass-card-border rounded-2xl overflow-y-auto">
              {isLoading ? (
                <div className="p-3 space-y-1">
                  {/* Loading skeletons */}
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-xl glass-surface-bg glass-surface-border animate-pulse"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded bg-muted/20 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted/20 rounded w-3/4" />
                          <div className="h-3 bg-muted/20 rounded w-1/2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayedPrompts.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {searchQuery.trim() ? (
                    <div>
                      <p className="text-sm">No prompts found</p>
                      <p className="text-xs mt-1">Try a different search term</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm">No prompts in this view</p>
                      {viewMode === 'user' && onCreatePrompt && (
                        <button
                          onClick={onCreatePrompt}
                          className="mt-3 text-sm text-primary hover:underline"
                        >
                          Create your first prompt
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 space-y-1">
                  {displayedPrompts.map(prompt => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      className={cn(
                        'w-full px-3 py-3 text-left rounded-xl',
                        'hover:glass-surface-hover transition-all duration-300',
                        'flex items-start gap-3',
                        selectedPrompt?.id === prompt.id && 'glass-surface'
                      )}
                    >
                      <PromptCategoryIcon
                        iconName={PROMPT_CATEGORY_ICONS[prompt.category]}
                        className="mt-0.5 flex-shrink-0"
                        size={18}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium text-foreground truncate flex-1">
                            {prompt.title}
                          </div>
                          <div
                            onClick={e => handleToggleFavorite(prompt, e)}
                            className="flex-shrink-0 cursor-pointer"
                            role="button"
                            aria-label={
                              isFavorite(prompt.id) ? 'Remove from favorites' : 'Add to favorites'
                            }
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggleFavorite(prompt, e as unknown as React.MouseEvent);
                              }
                            }}
                          >
                            <Star
                              className={cn(
                                'h-4 w-4 transition-colors duration-200',
                                isFavorite(prompt.id)
                                  ? 'text-yellow-500 fill-yellow-500'
                                  : 'text-muted-foreground hover:text-yellow-500'
                              )}
                            />
                          </div>
                        </div>
                        {prompt.description && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {prompt.description}
                          </div>
                        )}
                        <div className="mt-2">{getSourceBadge(prompt.source)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Detail panel */}
            <div className="flex-1 glass-card-bg glass-card-border rounded-2xl overflow-y-auto ml-3">
              {selectedPrompt ? (
                <div className="p-6 space-y-4">
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <PromptCategoryIcon
                            iconName={PROMPT_CATEGORY_ICONS[selectedPrompt.category]}
                            size={24}
                          />
                          <h3 className="text-xl font-semibold text-foreground">
                            {selectedPrompt.title}
                          </h3>
                        </div>
                        {selectedPrompt.description && (
                          <p className="text-sm text-muted-foreground">
                            {selectedPrompt.description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={e => handleToggleFavorite(selectedPrompt, e)}
                        className="p-2 hover:glass-surface rounded-xl transition-all duration-200"
                      >
                        <Star
                          className={cn(
                            'h-5 w-5 transition-colors duration-200',
                            isFavorite(selectedPrompt.id)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-muted-foreground'
                          )}
                        />
                      </button>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-2 mt-3">
                      {getSourceBadge(selectedPrompt.source)}
                      <span className="text-xs text-muted-foreground">
                        {PROMPT_CATEGORY_LABELS[selectedPrompt.category]}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <div className="text-sm font-medium text-foreground mb-2">Prompt Content</div>
                    <div className="p-4 glass-surface-bg glass-surface-border rounded-xl">
                      <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                        {selectedPrompt.content}
                      </pre>
                    </div>
                  </div>

                  {/* Variables */}
                  {selectedPrompt.variables && selectedPrompt.variables.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Variables</div>
                      <div className="space-y-2">
                        {selectedPrompt.variables.map(variable => (
                          <div
                            key={variable.name}
                            className="p-3 glass-surface-bg glass-surface-border rounded-xl flex items-start justify-between"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-foreground">
                                {`{{${variable.name}}}`}
                                {variable.required && (
                                  <span className="text-destructive ml-1">*</span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {variable.label}
                                {variable.placeholder && ` (e.g., ${variable.placeholder})`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-border">
                    {/* Only show "Use" button in chat context */}
                    {onSelectPrompt && (
                      <Button
                        onClick={handleUsePrompt}
                        variant="primary"
                        size="md"
                        className="flex-1"
                      >
                        <Sparkles className="h-4 w-4" />
                        <span>Use This Prompt</span>
                      </Button>
                    )}

                    {selectedPrompt.source !== 'system' && (
                      <>
                        {onEditPrompt && (
                          <Button
                            onClick={e => handleEdit(selectedPrompt, e)}
                            variant="outline"
                            size="md"
                          >
                            <Pencil className="h-4 w-4" />
                            <span>Edit</span>
                          </Button>
                        )}
                        <Button
                          onClick={e => handleDelete(selectedPrompt, e)}
                          variant="destructive"
                          size="md"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center p-8">
                  <div>
                    <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Select a prompt to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </Dialog>
  );
};
